import {
  CheckCircle,
  Clock,
  FileText,
  InfoIcon,
  MessageSquareIcon,
  Pause,
  Play,
  XCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";

// Import the new generic components
import {
  useDisconnectionRequestDetail,
  useUpdateDisconnectionRequest,
} from "@features/cx/disconnect/hooks";
import ReconnectDetailsTab from "@features/cx/reconnect/components/details/tabComponents/ReconnectDetailsTab";
import { RequestCommunications } from "@features/cx/shared/components/communicationLandingTab";
import { RejectRequestDialogue } from "@features/cx/shared/components/RejectRequestDialogue";
import { RequestHeader } from "@features/cx/shared/components/RequestHeader";
import { RequestSummaryCards } from "@features/cx/shared/components/RequestSummaryCards";
import { RequestQuickActions } from "@features/cx/shared/components/RequestQuickActions";
import { ActivitySidebar } from "@features/cx/shared/components/ActivitySidebar";
import { NotesTab } from "@features/cx/shared/components/tabs/NotesTab";
import { ServiceOrdersTab } from "@features/cx/shared/components/tabs/ServiceOrdersTab";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import {
  SERVICE_REQUEST_STATUS,
  SERVICE_REQUEST_STATUS_DISPLAY,
} from "@features/cx/shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Tabs } from "@shared/ui/tabs";
import { useEffect, useState } from "react";
import { Consumer } from "../../types";
import PlanActivateTab from "./tabComponents/PlanActivateTab";
import { formatTerritoryAddress } from "@shared/utils/address";
import { tr } from "zod/dist/types/v4/locales";

const ReconnectionRequestDetailPage = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: disconnectRequestData } = useDisconnectionRequestDetail(
    Number(id),
    remoteUtilityId,
  );
  const premise =
    disconnectRequestData?.territoryDataCodes?.service?.premise || "";

  const [currentStatus, setCurrentStatus] = useState<
    | "pending"
    | "in progress"
    | "completed"
    | "cancelled"
    | "draft"
    | "submitted"
    | "approved"
    | "rejected"
    | "on_hold"
  >("pending");

  // Update currentStatus when API data is loaded
  useEffect(() => {
    if (disconnectRequestData?.statusDisplay) {
      const statusFromAPI = disconnectRequestData.statusDisplay.toLowerCase();
      setCurrentStatus(
        statusFromAPI as
          | "pending"
          | "in progress"
          | "completed"
          | "cancelled"
          | "draft"
          | "submitted"
          | "approved"
          | "rejected"
          | "on_hold",
      );
    }
  }, [disconnectRequestData]);

  const { mutateAsync: updateDisconnectRequest } =
    useUpdateDisconnectionRequest(Number(id));

  const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Show error state
  if (!disconnectRequestData) {
    return <div className="flex items-center justify-center h-64"></div>;
  }

  const request = disconnectRequestData;
  // Status change handlers
  const handleAcknowledge = async () => {
    const payload = {
      status: SERVICE_REQUEST_STATUS.IN_PROGRESS,
    };
    await updateDisconnectRequest(payload);
    setCurrentStatus("in progress");
    toast({
      title: "Success",
      description: "Service request acknowledged and moved to In Progress!",
      variant: "default",
    });
  };

  const handlePutOnHold = () => {
    // update the status in the database
    updateDisconnectRequest({
      requestId: Number(id),
      payload: {
        status: SERVICE_REQUEST_STATUS.ON_HOLD,
      },
    });
    setCurrentStatus("on_hold");
    toast({
      title: "Success",
      description: "Service request put on hold!",
      variant: "default",
    });
  };

  const handleResume = () => {
    // update the status in the database
    updateDisconnectRequest({
      requestId: Number(id),
      payload: {
        status: SERVICE_REQUEST_STATUS.IN_PROGRESS,
      },
    });
    setCurrentStatus("in progress");
    toast({
      title: "Success",
      description: "Service request resumed and back in progress!",
      variant: "default",
    });
  };

  const handleResolveRequest = () => {
    // update the status in the database
    updateDisconnectRequest({
      requestId: Number(id),
      status: SERVICE_REQUEST_STATUS.COMPLETED,
      closeDate: new Date().toISOString(),
    });
    setCurrentStatus("completed");
    toast({
      title: "Success",
      description: "Service request resolved successfully!",
      variant: "default",
    });
    navigate("/cx/reconnections");
  };

  const handleRejectRequest = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      await updateDisconnectRequest({
        requestId: Number(id),
        status: SERVICE_REQUEST_STATUS.REJECTED,
        closeDate: new Date().toISOString(),
        closureRemark: reason || "Service request rejected by the consumer!",
      });
      setCurrentStatus("rejected");
      toast({
        title: "Success",
        description: "Service request rejected successfully!",
        variant: "default",
      });
      setRejectDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject service request.",
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
    }
  };

  // Action buttons for RequestHeader
  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.PENDING],
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.IN_PROGRESS],
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.ON_HOLD],
    },
  ];

  // Transform API data to match RequestHeader interface
  const transformedRequestForHeader = {
    id: request?.id?.toString() || "",
    name: request?.utilitySupportRequest?.name || "NA",
    type: request?.utilitySupportRequest?.supportRequestType || "NA",
    subType: request?.utilitySupportRequest?.supportRequestSubtype || "NA",
    reason: request?.requestType || "",
    dateRequested: request?.createdDate || "",
    requestNo: request?.requestNo || "",
    customerName:
      `${request?.consumer.firstName || ""} ${
        request?.consumer.lastName || ""
      }`.trim() || "Unknown Customer",
    accountNumber: request?.consumer.consumerNo || "",
    priority: request?.priorityLevelDisplay || 0,
    status: currentStatus,
    isVip: request?.consumer.isVip || false,
    sla: {
      status: request?.slaSummary?.slaStatus || "",
      dueDate: request?.slaSummary?.slaDueDate || "",
      remaining: request?.slaSummary?.timeRemaining || "",
    },
    priorityDisplay: request?.priorityLevelDisplay,
  };

  // Transform API data to match RequestSummaryCards interface
  const transformedRequestForSummary = {
    id: request?.id?.toString() || "NA",
    dateRequested: request?.createdDate || "NA",
    requestNo: request?.requestNo || "NA",
    requestedBy: request?.createdUserRemoteName || "NA",
    assignedTo: request?.assignedTo || "Unassigned",
    lastUpdatedOn: request?.lastModifiedDate || "NA",
    lastUpdatedBy: request?.lastModifiedUserRemoteName || "NA",
    categoryDisplay: request?.categoryDisplay || "NA",
    subCategoryDisplay: request?.subCategoryDisplay || "NA",
    resolvedOn: request?.closeDate || "NA",
    resolvedBy: request?.resolvedBy || "NA",
    customer: {
      masterAccountName:
        request?.consumer?.ownershipDetails?.approvalDetails?.masterAccountName,
      categoryDisplay: request?.categoryDisplay || "",
      subCategoryDisplay: request?.subCategoryDisplay || "",
      consumerNo: request.consumer.consumerNo || "",
      id: request.consumer.id?.toString() || "",
      firstName: request.consumer.firstName || "",
      lastName: request.consumer.lastName || "",
      phone: request.consumer.contactNumber,
      email: request.consumer.email,
      category: request.consumer.category,
      subCategory: request.consumer.subCategory,
      ownershipDisplay: request.consumer.ownershipDisplay,
      serviceAddress:
        formatTerritoryAddress(
          { service: request?.territoryData.service },
          "service",
        ) || "No address configured",
      billingAddress:
        formatTerritoryAddress(
          { billing: request?.territoryData.billing },
          "billing",
        ) || "No address configured",
    },
  };

  // Simplified request for QuickActions
  const requestForQuickActions = {
    id: request?.id.toString() || "",
    requestNo: request?.requestNo || "",
    customer: {
      id: request?.consumer.id?.toString() || "NA",
      firstName: request?.consumer.firstName || "NA",
      lastName: request?.consumer.lastName || "NA",
      phone: request?.consumer.contactNumber || "NA",
      email: request?.consumer.email || "NA",
      consumerNo: request?.consumer.consumerNo || "NA",
    },
  };

  return (
    <div className="space-y-4">
      {/* Main Content Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Column (3/4): Header, Summary, and Tabs */}
        <div className="lg:col-span-3 space-y-6">
          <RequestHeader
            request={transformedRequestForHeader}
            currentStatus={currentStatus.toLowerCase()}
            actionButtons={actionButtons}
            action="Reconnection"
          >
            <RequestSummaryCards request={transformedRequestForSummary} />

            {/* Tabs Section */}
            <div className="mt-6 border-t pt-4">
              <Tabs
                className="w-full"
                tabsListClassName="grid w-full grid-cols-6 mb-6"
                defaultValue="details"
                urlMapping={{
                  details: "details",
                  plan: "plan",
                  serviceOrders: "service-orders",
                  communication: "communication",
                  notes: "notes",
                  timeline: "timeline",
                }}
                tabComponents={{
                  details: {
                    label: "Details",
                    icon: <InfoIcon className="h-4 w-4" />,
                    component: (
                      <ReconnectDetailsTab
                        request={{
                          consumer: {
                            ...(request.consumer as unknown as Consumer),
                            serviceAddress:
                              transformedRequestForSummary.customer
                                .serviceAddress,
                          },
                          id: request.id,
                          requestNo: request.requestNo,
                          requestType: request.utilitySupportRequest?.name,
                          requestTypeDisplay: request.requestTypeDisplay,
                          remoteUtilityId: request.remoteUtilityId,
                          utilitySupportRequest: request.utilitySupportRequest,
                          status: Number(request.status),
                          statusDisplay: request.statusDisplay,
                          source: request.source,
                          sourceDisplay: request.sourceDisplay,
                          consumerRemark: request.consumerRemark,
                          createdDate: request.createdDate,
                          closeDate: request.closeDate,
                          prefferedTimeSlotDisplay:
                            request.prefferedTimeSlotDisplay,
                          additionalData: {
                            ...request.additionalData,
                            preferredTimeSlot:
                              request.additionalData?.preferredTimeSlot ?? 0,
                          },
                          createdUserRemoteName: request.createdUserRemoteName,
                          lastModifiedUserRemoteName:
                            request.lastModifiedUserRemoteName,
                          requestDate: request.requestDate,
                          actualAcknowledgeTime: request.actualAcknowledgeTime,
                          acknowledgementTimeAchieved:
                            request?.acknowledgementTimeAchieved?.toString() ||
                            "NA",
                          resolutionTimeAchieved:
                            request?.resolutionTimeAchieved?.toString() || "NA",
                          outstandingBalance: request.outstandingBalance,
                          priorityLevelDisplay: request.priorityLevelDisplay,
                          services: [],
                          slaSummary: {
                            slaTarget: {
                              acknowledgementTime: "2 hours",
                              responseTime: "4 hours",
                              resolutionTime: "24 hours",
                            },
                            slaAchieved: {
                              acknowledgementTime: "1.5 hours",
                              responseTime: "2.25 hours",
                              resolutionTime: "Pending",
                            },
                          },
                        }}
                      />
                    ),
                  },
                  plan: {
                    label: "Plan & Activate",
                    icon: <CheckCircle className="h-4 w-4" />,
                    component: (
                      <PlanActivateTab
                        premise={premise}
                        id={id}
                        consumerData={request}
                        currentStatus={currentStatus}
                      />
                    ),
                  },
                  serviceOrders: {
                    label: "Service Orders",
                    icon: <CheckCircle className="h-4 w-4" />,
                    component: (
                      <ServiceOrdersTab
                        consumerAccountNumber={
                          request.consumer.consumerNo || ""
                        }
                        consumerId={request.consumer.id?.toString() || ""}
                        consumerName={
                          request.consumer.firstName +
                          " " +
                          request.consumer.lastName
                        }
                        requestId={request.id?.toString() || ""}
                        remoteUtilityId={Number(remoteUtilityId) || 0}
                        currentStatus={currentStatus.toUpperCase()}
                      />
                    ),
                  },
                  communication: {
                    label: "Communication",
                    icon: <MessageSquareIcon className="h-4 w-4" />,
                    component: (
                      <RequestCommunications
                        entityId={request.id.toString()}
                        remoteUtilityId={Number(remoteUtilityId)}
                        email={request.consumer.email}
                        phone={request.consumer.contactNumber || ""}
                        action="consumer_support_request"
                      />
                    ),
                  },
                  notes: {
                    label: "Notes",
                    icon: <FileText className="h-4 w-4" />,
                    component: (
                      <NotesTab
                        source={null}
                        requestId={request.id.toString()}
                        remoteUtilityId={Number(remoteUtilityId)}
                      />
                    ),
                  },
                  timeLine: {
                    label: "Time Line",
                    icon: <Clock className="h-4 w-4" />,
                    component: (
                      <TimelineTab
                        remoteUtilityId={Number(remoteUtilityId)}
                        entityId={request.id.toString()}
                        actionType="reconnection"
                      />
                    ),
                  },
                }}
              />
            </div>
          </RequestHeader>
        </div>

        {/* Right Column (1/4): Quick Actions and Activity Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          <RequestQuickActions
            request={requestForQuickActions}
            currentStatus={currentStatus}
            actionButtons={actionButtons}
            handleResolveRequest={handleResolveRequest}
            handleRejectRequest={handleRejectRequest}
          />
          <ActivitySidebar
            entityId={request?.id?.toString() || ""}
            remoteUtilityId={Number(remoteUtilityId) || 0}
            actionType="reconnection"
          />
        </div>
      </div>

      <RejectRequestDialogue
        isOpen={isRejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={handleRejectConfirm}
        title="Reject Service Request"
        description="Please provide a reason for rejecting this service request."
        label="Rejection Reason"
        placeholder="Please provide a reason for rejecting this request..."
        rejectButtonText="Reject Request"
        cancelButtonText="Cancel"
        isSubmitting={isRejecting}
        icon={<XCircle className="h-5 w-5 text-red-600" />}
      />
    </div>
  );
};

export default ReconnectionRequestDetailPage;
